"use client";

import { c as _c } from "react/compiler-runtime";
import { CipherText, Flex, FlexColumn, Media, TextLabel } from "@opensea/ui-kit";
import { useMountEffect, useOnChange } from "@opensea/ui-kit/hooks";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { CONSTELLATION_BACKGROUND_ROOT_ID, ConstellationBackgroundStoreProvider, useSelectConstellationBackgroundStore } from "@/components/constellation-background/ConstellationBackgroundStore";
import { ConstellationTrendingCollectionsBackground } from "@/components/constellation-background/presets/ConstellationTrendingCollectionsBackground";
import { IntroStepLogo } from "./intro/IntroStepLogo";
import { StepContainer, useStepContext } from "./Step";
const intervalDivisor = 4;
const intervalPerCharacter = 125;
const easingStages = [0.3, 0.2, 0.4];
function getRandomDuration(base) {
  return base * (0.6 + Math.random() * 0.2);
}
function OutroStepCopy(t0) {
  const $ = _c(8);
  const {
    copyText
  } = t0;
  const [active, setActive] = useState(true);
  let t1;
  if ($[0] !== copyText.length) {
    t1 = () => {
      const duration = getRandomDuration(copyText.length * intervalPerCharacter);
      const animateInDuration = getRandomDuration(copyText.length * (intervalPerCharacter / intervalDivisor));
      const timeout = setTimeout(() => {
        setActive(false);
      }, duration - animateInDuration);
      return () => {
        setActive(true);
        clearTimeout(timeout);
      };
    };
    $[0] = copyText.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== copyText) {
    t2 = [copyText];
    $[2] = copyText;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getRandomDuration(intervalPerCharacter / intervalDivisor);
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== active || $[6] !== copyText) {
    t4 = <TextLabel className="text-center" size="md"><CipherText active={active} interval={t3} key={copyText}>{copyText}</CipherText></TextLabel>;
    $[5] = active;
    $[6] = copyText;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function OutroStepProgressBar(t0) {
  const $ = _c(10);
  const {
    copy
  } = t0;
  const {
    presented
  } = useStepContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: "0%",
      transitionDuration: "0ms"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [progressStyle, setProgressStyle] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const timeoutsRef = useRef(t2);
  let t3;
  if ($[2] !== copy) {
    t3 = easingStages.map(stage => getRandomDuration((copy.join(" ").length - (copy.at(-1)?.length || 0)) * intervalPerCharacter * stage / 2));
    $[2] = copy;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const progressBarDurations = t3;
  let t4;
  let t5;
  if ($[4] !== presented || $[5] !== progressBarDurations) {
    t4 = () => {
      if (presented) {
        let totalDuration = 0;
        easingStages.forEach((_, index) => {
          const timeout = setTimeout(() => {
            setProgressStyle({
              width: `${((index + 1) * 33.3).toString()}%`,
              transitionDuration: `${progressBarDurations[index].toString()}ms`
            });
          }, totalDuration);
          timeoutsRef.current.push(timeout);
          totalDuration = totalDuration + progressBarDurations[index];
          totalDuration;
        });
      }
      return () => {
        timeoutsRef.current.forEach(_temp);
        timeoutsRef.current = [];
      };
    };
    t5 = [presented, progressBarDurations];
    $[4] = presented;
    $[5] = progressBarDurations;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== progressStyle) {
    t6 = <Flex className="h-2 w-1/4 min-w-64 overflow-hidden rounded-full bg-white/15"><Flex className="h-full bg-white ease-in-out" style={progressStyle} /></Flex>;
    $[8] = progressStyle;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function _temp(timeout_0) {
  return clearTimeout(timeout_0);
}
export function OutroStep(props) {
  const [index, setIndex] = useState(0);
  const [complete, setComplete] = useState(false);
  const t = useTranslations("OutroStep");
  const copy = [...[t("aggregatingListings"), t("addingTokens", {
    count: 19
  }), t("enablingCrossChainPurchasing"), t("chartingInitialVoyages")].map(c => `${c}...`), t("bootSequenceComplete")];
  function onComplete() {
    props.onConfirm();
  }
  async function startAnimation() {
    for (let i = 0; i < copy.length; i++) {
      setIndex(i);

      // Hide icon background on last
      if (i === copy.length - 1) {
        setComplete(true);
      }
      await new Promise(resolve => {
        setTimeout(resolve, getRandomDuration(copy[i].length * intervalPerCharacter * (i === copy.length - 1 ? 1.5 : 1) / 2));
      });
    }
    onComplete();
  }
  useOnChange(props.presented, () => {
    if (props.presented) {
      startAnimation().catch(error => console.error(error));
    }
  });
  return <ConstellationBackgroundStoreProvider>
      <StepContainer background={<Media greaterThanOrEqual="md">
            <OutroStepBackground complete={complete} presented={props.presented} />
          </Media>} className="overflow-y-hidden" {...props}>
        <FlexColumn className="w-full items-center gap-10">
          <FlexColumn className="w-full items-center gap-8">
            <IntroStepLogo />
            <OutroStepProgressBar copy={copy} />
          </FlexColumn>
          <Flex className="min-h-5">
            {props.presented ? <OutroStepCopy copyText={copy[index]} /> : null}
          </Flex>
        </FlexColumn>
      </StepContainer>
    </ConstellationBackgroundStoreProvider>;
}
function OutroStepBackground(t0) {
  const $ = _c(9);
  const {
    complete,
    presented
  } = t0;
  const [setOpen, setBackgroundVisibility, setBackgroundAnimationOptions] = useSelectConstellationBackgroundStore(useShallow(_temp2));
  let t1;
  if ($[0] !== setBackgroundAnimationOptions) {
    t1 = () => {
      setBackgroundAnimationOptions(CONSTELLATION_BACKGROUND_ROOT_ID, {
        loopSpeedMultiplier: 15
      });
    };
    $[0] = setBackgroundAnimationOptions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useMountEffect(t1);
  let t2;
  if ($[2] !== presented || $[3] !== setOpen) {
    t2 = () => {
      if (presented) {
        setOpen(presented);
      }
    };
    $[2] = presented;
    $[3] = setOpen;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useOnChange(presented, t2);
  let t3;
  if ($[5] !== complete || $[6] !== setBackgroundVisibility) {
    t3 = () => {
      if (complete) {
        setBackgroundVisibility(CONSTELLATION_BACKGROUND_ROOT_ID, false);
      }
    };
    $[5] = complete;
    $[6] = setBackgroundVisibility;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useOnChange(complete, t3);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ConstellationTrendingCollectionsBackground />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function _temp2(state) {
  return [state.setOpen, state.setBackgroundVisibility, state.setBackgroundAnimationOptions];
}